import { useContext, useEffect, useState } from "react";
import { ActivityAnimationContext } from "./ActivityAnimationContext";

type Props = {
  children: string;
};

const charactersPerSecond = 90;

function sharedPrefixLength(a: string, b: string) {
  let length = 0;
  while (length < a.length && a[length] === b[length]) length++;
  return length;
}

function RevealingText({ children: text }: Props) {
  const [progress, setProgress] = useState({ text, length: 0 });
  if (progress.text !== text) {
    setProgress({
      text,
      length: Math.min(
        progress.length,
        sharedPrefixLength(progress.text, text)
      ),
    });
  }
  const revealing = progress.length < text.length;

  useEffect(() => {
    if (!revealing) return;
    let frame: number;
    let lastTime: number | null = null;
    const tick = (time: number) => {
      if (lastTime === null) lastTime = time;
      const count = Math.floor(
        ((time - lastTime) * charactersPerSecond) / 1000
      );
      if (count > 0) {
        lastTime += (count * 1000) / charactersPerSecond;
        setProgress((current) => ({
          ...current,
          length: Math.min(current.text.length, current.length + count),
        }));
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [revealing]);

  return text.slice(0, progress.length);
}

export function TypewriterSpan({ children: text }: Props) {
  const activity = useContext(ActivityAnimationContext);
  const [reducedMotion] = useState(
    () => window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );

  return activity?.live && !reducedMotion ? (
    <RevealingText key={activity.activityId}>{text}</RevealingText>
  ) : (
    text
  );
}
